import { Injectable } from '@angular/core';

@Injectable()
export class PrintService {

  constructor() { }

  /**
   * Print given elements to PDF or window.print()
   * 
   * @param printElements Array of HTMLElements to print
   * @param toPdf Boolean to decide if result should be PDF or window.print()
   * @param pdfFilename PDF filename (without .pdf)
   * @returns Promise<void>
   */
  public async print(printElements: HTMLElement[], toPdf: boolean = false, pdfFilename: string = 'report') {

    let printContainer = document.getElementById('print-container') as HTMLElement | null;
    if (!printContainer) {
      printContainer = document.createElement('div');
      printContainer.id = 'print-container';
    }

    printContainer.className = toPdf ? 'pdf' : 'd-print-block d-none print-area';
    printContainer.innerHTML = '';

    const frag = document.createDocumentFragment();
    for (const el of printElements) {
      const clone = el.cloneNode(true) as HTMLElement;
      this.persistFormValuesIntoClone(el, clone);
      frag.appendChild(clone);
    }
    printContainer.appendChild(frag);
    document.body.appendChild(printContainer);

    if (!toPdf) {
      requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
      return;
    }

    await this.sanitizeAndInlineAssets(printContainer);
    await this.printIframe(printContainer, pdfFilename);

    printContainer.className = 'd-none';
  }

  /**
   * Build an iframe with the given element's content, and use html2pdf.js to render to PDF
   * 
   * @param sourceEl HTMLElement to print
   * @param filename Name of PDF file (without .pdf)
   * @return Promise<void>
   */
  public async printIframe(sourceEl: HTMLElement, filename = 'document'): Promise<void> {
    const clone = sourceEl.cloneNode(true) as HTMLElement;

    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]') as NodeListOf<HTMLLinkElement>)
      .map(l => `<link rel="stylesheet" href="${l.href}">`).join('');
    const inlineStyles = Array.from(document.querySelectorAll('style')).map(s => s.outerHTML).join('');

    const PDF_STYLE = `
      .form-element--bordered {
        padding-top: 15px !important;
      }
      .form-section--clip-top,
      .form-section--clip-middle,
      .form-section--clip-bottom { display: none !important; }

      .z-depth-1.form-section,
      .form-section--bottom,
      .form-section {
        position: static !important;
        transform: none !important;
        z-index: auto !important;
        box-shadow: none !important;
        height: auto !important;
      }

      .form-element h1,
      .form-element h2,
      .form-element h3,
      .form-element h4,
      .form-element h5,
      .form-element h6 { display:block !important; position:static !important; line-height:1 !important; }
      .form-section, .form-element, .form-block, .form-block--element, .mb-1.form-block.col { display:block !important; }

      input[type="checkbox"], input[type="radio"] {
        appearance: auto !important;
        -webkit-appearance: auto !important;
        width: 14px; height: 14px;
        display: inline-block !important;
        visibility: visible !important;
      }

      .form-check-input[type=radio]:not(:checked) + label,
      .form-check-input[type=radio]:checked + label,
      label.btn input[type=radio]:not(:checked) + label,
      label.btn input[type=radio]:checked + label {
        position: relative;
        padding-left: 35px;
        cursor: pointer;
        display: inline-block;
        transition: .28s ease;
        user-select: none;
      }

      .no-break,
      .form-block--element {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        -webkit-column-break-inside: avoid !important;
      }

      #print-root { box-sizing: border-box; background:#fff; }

      .form-section__heading {
        display:block !important;
        position:static !important;
        transform:none !important;
        float:none !important;
        clear:both !important;
        padding:0 !important;
        z-index:auto !important;
      }
      .form-section__heading,
      .form-section__heading + * {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      .md-form > label,
      multiple-choice-control > div > label { display:block !important; }

      .form-preview {
        break-before: page !important;
        page-break-before: always !important;
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        margin: 0 !important;
      }

      .form-preview text-input-control {
        display:block !important;
        margin-top:0 !important;
        margin-bottom:0 !important;
        width:100%;
      }
      .form-preview text-input-control .md-form,
      .form-preview text-input-control .form-group {
        margin-top:0 !important;
        margin-bottom:.25rem !important;
        padding-top:0 !important;
        padding-bottom:0 !important;
      }
      .form-preview text-input-control .form-control,
      .form-preview text-input-control input[type="text"],
      .form-preview text-input-control textarea {
        margin-top:0 !important;
        margin-bottom:0 !important;
      }
      .form-element text-input-control:first-child,
      .form-block   text-input-control:first-child { margin-top:0 !important; }
      .form-element text-input-control:last-child,
      .form-block   text-input-control:last-child  { margin-bottom:0 !important; }

      @media print {
        html, body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background:#fff;
        }
        * { box-shadow:none !important; }
        .no-break { display:block !important; overflow:visible !important; }
        img, svg, canvas { max-width:100%; height:auto; }

        .form-preview text-input-control,
        text-input-control div,
        text-input-control div div,
        text-input-control div div label,
        .form-block--element,
        .form-block {
          margin-top:0 !important;
          margin-bottom:0 !important;
          padding:0 !important;
        }
        label {
          width: 100% !important;         
          white-space: normal !important;  
          overflow: visible !important;
        }

        .md-form > label {
          position: static !important;
          transform: none !important;
          display: block !important;
          float: none !important;
          overflow: visible !important;
          margin: 0 0 .25rem !important;
          line-height: 1.2 !important;
        }

        .form-preview[id^="action-items-"],
        .form-preview[id^="notes-"],
        .form-preview[style*="page-break-before"] {
          break-before: page !important;
          page-break-before: always !important;
          margin-top:0 !important;
          padding-top:0 !important;
          display:block !important;
          position:static !important;
          transform:none !important;
          box-shadow:none !important;
          overflow:visible !important;
        }

        .form-preview .report-header,
        .form-preview .report-header + * {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          -webkit-column-break-inside: avoid !important;
        }

        .form-preview .row,
        .form-preview .d-print-flex { display:block !important; }

        .z-depth-1, .z-depth-2, .z-depth-3, .z-depth-4, .z-depth-5 {
          position:static !important;
          transform:none !important;
          box-shadow:none !important;
          overflow:visible !important;
        }

        .form-preview[id^="action-items-"]::before,
        .form-preview[id^="notes-"]::before,
        .form-preview[style*="page-break-before"]::before {
          content:"";
          display:table;
          clear:both;
        }

        text-input-control,
        text-input-control input {
          width: 100%;
          overflow: visible;
        }

        .form-preview mdb-card-body,
        .form-preview .card-body {
          overflow: visible !important;
        }

        .form-preview .md-form,
        .form-preview .md-form > label {
          overflow: visible !important;
        }

        .form-preview .md-form > label {
          position: static !important;
          display: block !important;
          float: none !important;
          width: auto !important;
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: ellipsis !important;
          transform: none !important;
          margin: 0 0 .25rem !important;
          line-height: 1.2 !important;
        }

        .form-preview text-input-control .md-form > label,
        .form-preview text-input-control label {
          position: static !important;
          display: block !important;
          transform: none !important;
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: ellipsis !important;
          max-width: 100% !important;
          padding-right: 3px !important;   /* small buffer for raster rounding */
          margin-right: 0 !important;
          overflow-wrap: break-word !important;
          line-height: 1.2 !important;
          text-wrap: wrap !important;
        }

        #print-root, #print-root * { box-sizing: border-box !important; }

        .form-preview .row,
        .form-preview [class^="col-"],
        .form-preview .col {
          overflow: visible !important;
        }
      }
    `;

    const token = `h2p_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const targetOrigin = (location.origin && location.origin !== 'null') ? location.origin : '*';
    const template = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <base href="${location.origin}${location.pathname}">
        ${links}${inlineStyles}
        <style>${PDF_STYLE}</style>
      </head>
      <body>
        <div id="print-root">${clone.outerHTML}</div>
        <script src="https://unpkg.com/html2pdf.js@0.10.1/dist/html2pdf.bundle.min.js"><\/script>
        <script>
          (async function () {
            const TOKEN = ${JSON.stringify(token)};
            const TARGET_ORIGIN = ${JSON.stringify(targetOrigin)};
            const el = document.getElementById('print-root');

            const ignoreElements = (node) => {
              try {
                if (!(node instanceof Element)) return false;
                if (node.hasAttribute('data-html2canvas-ignore')) return true;
                if (node.tagName === 'IMG') {
                  const src = node.getAttribute('src') || '';
                  if (!src) return false;
                  const a = document.createElement('a');
                  a.href = src; return a.origin !== location.origin;
                }
                const bg = getComputedStyle(node).backgroundImage || '';
                if (bg && bg.includes('url(')) {
                  const urls = [];
                  bg.replace(/url\\(([^)]+)\\)/g, (_, m) => {
                    const u = m.trim().replace(/^['"]|['"]$/g, ''); urls.push(u); return '';
                  });
                  for (const u of urls) {
                    const a = document.createElement('a'); a.href = u;
                    if (a.origin && a.origin !== location.origin) return true;
                  }
                }
              } catch {}
              return false;
            };

            const opt = {
              margin: 0,
              filename: ${JSON.stringify(filename)} + '.pdf',
              pagebreak: { mode: ['css'], avoid: ['.no-break', '.form-block--element'] },
              image: { type: 'jpeg', quality: 0.92 },
              html2canvas: { scale: 1, useCORS: true, allowTaint: false, ignoreElements },
              jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
            };

            try {
              if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch(e){} }
              const blob = await html2pdf().set(opt).from(el).outputPdf('blob');
              parent.postMessage({ __h2p_done: true, blob, __h2p_token: TOKEN }, TARGET_ORIGIN);
            } catch (e) {
              parent.postMessage({ __h2p_error: String(e), __h2p_token: TOKEN }, TARGET_ORIGIN);
            }
          })();
        <\/script>
      </body>
    </html>
    `;

    let iframe: HTMLIFrameElement | null = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:0;height:0;border:0;visibility:hidden;';
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    document.body.appendChild(iframe);

    const cleanups: Array<() => void> = [];
    const cleanup = () => { while (cleanups.length) { try { cleanups.pop()!(); } catch {} } };

    try {
      const blob: Blob = await new Promise((resolve, reject) => {
        const onMsg = (ev: MessageEvent) => {
          if (!iframe || ev.source !== iframe.contentWindow) return;
          const data = ev.data || {};
          if (data.__h2p_token !== token) return;
          if (data.__h2p_done && data.blob) {
            window.removeEventListener('message', onMsg);
            resolve(data.blob as Blob);
          } else if (data.__h2p_error) {
            window.removeEventListener('message', onMsg);
            reject(new Error(String(data.__h2p_error)));
          }
        };
        window.addEventListener('message', onMsg);
        cleanups.push(() => window.removeEventListener('message', onMsg));

        const tid = window.setTimeout(() => {
          window.removeEventListener('message', onMsg);
          reject(new Error('PDF generation timed out'));
        }, 30000);
        cleanups.push(() => clearTimeout(tid));

        iframe!.srcdoc = template; // start work
      });

      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } finally {
      cleanups.push(() => {
        if (!iframe) return;
        try { iframe.srcdoc = ''; iframe.src = 'about:blank'; } catch {}
        const ref = iframe; iframe = null;
        requestAnimationFrame(() => ref.remove());
      });
      cleanup();
    }
  }

  /**
   * Add values from original form elements into the cloned elements
   * 
   * @param srcRoot Original form elements
   * @param dstRoot Cloned form elements
   * @returns void
   */
  private persistFormValuesIntoClone(srcRoot: ParentNode, dstRoot: ParentNode) {
    const srcInputs = Array.from(srcRoot.querySelectorAll<HTMLInputElement>('input'));
    const dstInputs = Array.from(dstRoot.querySelectorAll<HTMLInputElement>('input'));

    srcInputs.forEach((src, i) => {
      const dst = dstInputs[i];
      if (!dst) return;

      const type = (src.getAttribute('type') || 'text').toLowerCase();
      if (type === 'checkbox' || type === 'radio') {
        dst.checked = src.checked;
        if (dst.checked) dst.setAttribute('checked', '');
        else dst.removeAttribute('checked');
        if (src.name) dst.name = src.name;
        if (src.value != null) dst.value = src.value;
      } else {
        dst.value = src.value ?? '';
        dst.setAttribute('value', dst.value);
      }
    });

    const srcTextareas = Array.from(srcRoot.querySelectorAll<HTMLTextAreaElement>('textarea'));
    const dstTextareas = Array.from(dstRoot.querySelectorAll<HTMLTextAreaElement>('textarea'));
    srcTextareas.forEach((src, i) => {
      const dst = dstTextareas[i];
      if (!dst) return;
      const v = src.value ?? '';
      dst.value = v;
      dst.textContent = v;
    });

    const srcSelects = Array.from(srcRoot.querySelectorAll<HTMLSelectElement>('select'));
    const dstSelects = Array.from(dstRoot.querySelectorAll<HTMLSelectElement>('select'));
    srcSelects.forEach((src, i) => {
      const dst = dstSelects[i];
      if (!dst) return;
      dst.value = src.value;
      Array.from(dst.options).forEach((opt, idx) => {
        const selected = src.options[idx]?.selected ?? false;
        opt.selected = selected;
        if (selected) opt.setAttribute('selected', '');
        else opt.removeAttribute('selected');
      });
    });
  }

  /**
   * Sanitize and Inline assets on an HTMLElement
   * 
   * @param root HTMLElement to sanitize and inline assets within
   * @return Promise<void>
   */
  private async sanitizeAndInlineAssets(root: HTMLElement) {
    root.querySelectorAll('button, input[type="button"], input[type="submit"], iframe, video, canvas')
        .forEach(el => el.setAttribute('data-html2canvas-ignore', 'true'));

    Array.from(root.querySelectorAll('textarea')).forEach(ta => {
      const p = document.createElement('p');
      p.textContent = (ta as HTMLTextAreaElement).value ?? '';
      p.style.paddingTop = '1rem';
      ta.parentElement?.replaceChild(p, ta);
    });
    Array.from(root.querySelectorAll('.d-print-none')).forEach(el => {
      let childLink = el.querySelector('fa-icon[icon="file-download"]');
      if (!childLink){el.remove();}
    })
    Array.from(root.querySelectorAll('text-input-control.ng-star-inserted')).forEach(el => {
      let parent = el.parentElement.closest('.ng-star-inserted[style="width: 25%;"]');
      if (parent) {
        parent.setAttribute("style", "width: 100%;");
      }
    })
    const imgs = Array.from(root.querySelectorAll('img'));
    for (const img of imgs) {
      const src = img.getAttribute('src') || '';
      if (!src || src.startsWith('data:') || src.startsWith('blob:')) continue;
      try {
        const abs = new URL(src, document.baseURI).href;
        // Try fetch; if CORS blocks, this will throw
        const res = await fetch(abs, { mode: 'cors', credentials: 'omit', cache: 'no-store'});
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} fetching ${abs}`);
        }
        const blob = await res.blob();
        const b64 = await new Promise<string>(resv => {
          const fr = new FileReader(); fr.onload = () => resv(fr.result as string);
          fr.readAsDataURL(blob);
        });
        img.removeAttribute('crossorigin'); // not needed anymore
        img.src = b64;
      } catch (e) {
        // If we cannot fetch (CORS), hide to avoid taint
        console.log('Error: ' + e);
        img.setAttribute('data-html2canvas-ignore', 'true');
      }
    }
  }
}
